import { HandwritingSource } from "./handwriting-source.js";
import { passGate } from "./lianxi-gate.js";
import { fetchStrokeFile } from "./stroke-file.js";
import { localMarker } from "./xiezi-check.js";
import { XieziDrill } from "./xiezi-drill.js";
import { drillStart } from "../handwriting/xiezi-stages.js";
import { drillParts, part, wireControls } from "./xiezi-parts.js";
import {
  type DrillSounds,
  type SoundsFor,
  XieziSounds,
} from "./xiezi-sounds.js";
import { XieziSquare } from "./xiezi-square.js";
import { drillData } from "./xiezi-block-data.js";
import { openWhenNamed } from "./xiezi-named.js";

/**
 * Starting the writing drill on every block a 词典 entry carries.
 *
 * The block is a `<details>`, and a canvas inside a closed one has no size to
 * draw into. The outlines are fetched and the square built when the reader
 * opens it, which is also what keeps the fetch off the page of every reader
 * who does not. `xiezi-drill.ts` is the drill once it is built, and
 * `src/cidian-page/cidian-xiezi-markup.ts` is the block as the page draws it.
 *
 * **A visitor stops part way.** For a reader without an account the drill
 * shows the gate once a pass takes them past the taste
 * `src/handwriting/xiezi-taste.ts` allows, which `src/site/lianxi-gate.ts`
 * shows.
 */

/** The block, carrying the character and where its files are. */
const drillSelector = "details[data-cb-xiezi]";

/**
 * Build the drill inside one block that a reader has just opened.
 *
 * A block whose outlines cannot be fetched is left as the markup drew it,
 * which says what the drill is, the way the figure above it is left as the
 * character in the web font.
 */
async function openDrill(
  block: HTMLElement,
  fetching: typeof fetch,
  soundsFor: SoundsFor,
): Promise<void> {
  const data = drillData(block);
  const box = part(block, "square");
  const canvas = part(block, "pad");
  const strokes = await fetchStrokeFile(
    data.strokesUrl,
    data.character,
    fetching,
  );

  if (!box || !(canvas instanceof HTMLCanvasElement) || !strokes) {
    return;
  }

  // The square tells the drill about each stroke, and the drill is built
  // around the square, so the square is handed somewhere the drill will be.
  const holder: { drill?: XieziDrill } = {};
  const square = new XieziSquare({
    box,
    canvas,
    data: strokes,
    onStroke: (): void => {
      holder.drill?.strokeLanded();
    },
  });
  const parts = drillParts(block, square);

  if (!parts) {
    return;
  }

  const held = {
    character: data.character,
    strokes,
    prompt: data.prompt,
    recordings: data.recordings,
  };
  const drill = new XieziDrill({
    parts,
    ...held,
    sounds: soundsFor(block.ownerDocument),
    marker: localMarker(
      new HandwritingSource({ url: data.indexUrl, fetch: fetching }),
    ),
    onPass: passGate(block),
  });

  holder.drill = drill;
  block.dataset["cbXieziReady"] = "";
  wireControls(
    block,
    {
      undo: () => drill.undo(),
      clear: () => drill.clear(),
      check: () => drill.check(),
      // Again is the character shown afresh, from the first stage.
      again: () => drill.show({ ...held, progress: drillStart() }),
    },
    square,
  );
}

/**
 * Start the drill on every block under `root`, on the first open of each.
 *
 * **A block the address names is opened**, when the page loads and whenever
 * a link on it changes the fragment. `xiezi-named.ts` has why.
 *
 * @param root - The page, or the part of it to look in.
 * @param fetching - How to fetch. The window's own unless a test says otherwise.
 * @param named - The address's fragment, `#` and all. The window's own unless
 * a test says otherwise.
 * @param soundsFor - What plays the drill's sounds. The browser's own output
 * unless a test says otherwise.
 */
export function startXiezi(
  root: ParentNode,
  fetching: typeof fetch = (input, init): Promise<Response> =>
    fetch(input, init),
  named: string = location.hash,
  soundsFor: SoundsFor = (page): DrillSounds => new XieziSounds(page),
): void {
  const blocks = [...root.querySelectorAll<HTMLDetailsElement>(drillSelector)];

  openWhenNamed(blocks, named);

  for (const block of blocks) {
    let opened = block.open;

    if (opened) {
      void openDrill(block, fetching, soundsFor);
    }

    block.addEventListener("toggle", () => {
      if (block.open && !opened) {
        opened = true;
        void openDrill(block, fetching, soundsFor);
      }
    });
  }
}
